// UserProvider.js
import React, { useCallback, useEffect, useState, useMemo } from "react";
import UserContext from "./UserContext";
import userService from "../../Services/userService";
import {
  clearImageSasTokenCache,
  getImageSasTokenSilently,
} from "../../Services/imageSasToken";

export const UserProvider = ({ children }) => {
  const [userData, setUserData] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [imageSasToken, setImageSasToken] = useState(null);
  const [wellnessAccepted, setWellnessAccepted] = useState(false);

  const userPermissions = useMemo(() => {
    if (
      !userData?.userInfo?.userRole ||
      !userData?.entityInformation?.entityRoles
    ) {
      return [];
    }

    const matchingRole = userData.entityInformation.entityRoles.find(
      (entityRole) => entityRole.role === userData.userInfo.userRole,
    );

    return matchingRole?.permissions || [];
  }, [userData?.userInfo?.userRole, userData?.entityInformation?.entityRoles]);

  useEffect(() => {
    const handleStorage = (e) => {
      if (e.key !== "ow-dark-theme" || e.newValue === null) return;
      const next = e.newValue === "true";
      setUserData((prev) => {
        if (!prev?.userInfo || prev.userInfo.darkTheme === next) return prev;
        return { ...prev, userInfo: { ...prev.userInfo, darkTheme: next } };
      });
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  const resetSession = useCallback(() => {
    clearImageSasTokenCache();
    setUserData(null);
    setAccessToken(null);
    setImageSasToken(null);
    setWellnessAccepted(false);
  }, []);

  const contextValue = useMemo(
    () => ({
      userData,
      setUserData,
      accessToken,
      setAccessToken,
      imageSasToken,
      setImageSasToken,
      userPermissions,
      wellnessAccepted,
      setWellnessAccepted,
      resetSession,
    }),
    [userData, accessToken, imageSasToken, userPermissions, wellnessAccepted],
  );

  useEffect(() => {
    if (userData?.entityInformation?.entityPreference?.wellnessEnabled) {
      setWellnessAccepted(false);
    } else {
      setWellnessAccepted(true);
    }
  }, [userData?.entityInformation]);

  useEffect(() => {
    if (
      !userData?.userInfo?.userRole ||
      userData.userInfo.userRole === "4SightAdmin"
    )
      return;

    let cancelled = false;

    getImageSasTokenSilently()
      .then((token) => {
        if (!cancelled) setImageSasToken(token);
      })
      .catch((error) => {
        console.log("Failed to fetch image SAS token:", error);
      });

    return () => {
      cancelled = true;
    };
  }, [userData?.userInfo?.userRole, userData?.userInfo?.id]);

  return (
    <UserContext.Provider value={contextValue}>{children}</UserContext.Provider>
  );
};
